import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarEvent, CalendarInfo, CalendarSource } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { addDays, addMonths, clockOf, dayKey, eventDays, eventsByDay, isAllDayLike, layoutDay, minutesOn, monthGrid, parseDay, todayKey, weekDays, type DayKey } from '../lib/calendar';
import { language, locale, t, useLanguage } from '../lib/i18n';
import { EventCard, EventEditor, FloatingCard, lookup, type CalendarLookup, type EditorRequest } from './CalendarEvents';
import { Icon } from './Icon';
import { Mascot } from './Mascot';
import { toast } from './Toasts';

/**
 * The calendar: every calendar of every calendar account merged into one view (month / week / agenda), with a mini month and the
 * calendar list on the left. Events come from the server's cache (api().calendar.events) and are refetched when the server says
 * calendars changed, when the window regains focus, and after edits.
 */

export type CalMode = 'month' | 'week' | 'agenda';
const MODE_KEY = 'momomail.calendar.mode';
const HOUR_PX = 48;
const AGENDA_DAYS = 60;
/** Same choices as mail account colors. */
export const CALENDAR_COLORS = ['#5b3df5', '#ff4f6d', '#1fc98f', '#17b6d6', '#f5b800', '#ff8a3d', '#c04bd8', '#5d5987'];

const weekStart = () => (language() === 'zh' ? 1 : 0);

function loadMode(): CalMode {
  try {
    const m = localStorage.getItem(MODE_KEY);
    return m === 'week' || m === 'agenda' ? m : 'month';
  } catch {
    return 'month';
  }
}

export function monthTitle(key: DayKey): string {
  const d = parseDay(key);
  return language() === 'zh' ? `${d.getFullYear()}年${d.getMonth() + 1}月` : d.toLocaleDateString(locale(), { year: 'numeric', month: 'long' }); // i18n-ok
}

function weekdayNames(): string[] {
  const fmt = new Intl.DateTimeFormat(locale(), { weekday: 'short' });
  // 2026-10-04 is a Sunday
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2026, 9, 4 + ((i + weekStart()) % 7))));
}

function dayTitle(key: DayKey): string {
  const d = parseDay(key);
  return d.toLocaleDateString(locale(), { month: 'long', day: 'numeric', weekday: 'long' });
}

/** Range of days to fetch for what's on screen. */
function visibleRange(mode: CalMode, cursor: DayKey): { from: DayKey; to: DayKey } {
  if (mode === 'month') {
    const grid = monthGrid(cursor, weekStart());
    return { from: grid[0]!, to: addDays(grid[41]!, 1) };
  }
  if (mode === 'week') {
    const days = weekDays(cursor, weekStart());
    return { from: days[0]!, to: addDays(days[6]!, 1) };
  }
  return { from: cursor, to: addDays(cursor, AGENDA_DAYS) };
}

/** Window event (detail: 'YYYY-MM-DD') that moves the calendar to that day — 馍馍's "open this day" buttons. */
export const CALENDAR_GOTO = 'momomail:calendar-goto';

export function CalendarView({ onOpenSettings }: { onOpenSettings: () => void }) {
  useLanguage();
  const [info, setInfo] = useState<CalendarInfo | null>(null);
  const [mode, setMode] = useState<CalMode>(loadMode);
  const [cursor, setCursor] = useState<DayKey>(todayKey);
  useEffect(() => {
    const go = (e: Event) => {
      const day = (e as CustomEvent<string>).detail;
      if (/^\d{4}-\d{2}-\d{2}$/.test(day)) setCursor(day);
    };
    window.addEventListener(CALENDAR_GOTO, go);
    return () => window.removeEventListener(CALENDAR_GOTO, go);
  }, []);
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ event: CalendarEvent; anchor: DOMRect } | null>(null);
  const [dayList, setDayList] = useState<{ day: DayKey; anchor: DOMRect } | null>(null);
  const [editor, setEditor] = useState<EditorRequest | null>(null);
  const [syncing, setSyncing] = useState(false);
  const req = useRef(0);
  const range = visibleRange(mode, cursor);
  const look = useMemo(() => lookup(info), [info]);

  const loadInfo = useCallback(async () => {
    try {
      setInfo(await api().calendar.info());
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  const loadEvents = useCallback(async () => {
    const id = ++req.current;
    try {
      const list = await api().calendar.events(range.from, range.to);
      if (id !== req.current) return;
      setEvents(list);
      setError(null);
    } catch (e) {
      if (id === req.current) setError(errorText(e));
    }
  }, [range.from, range.to]);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);
  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);
  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  // The server synced or someone edited elsewhere: refetch; coming back to the window: refetch too
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void loadInfo();
        void loadEvents();
      }, 300);
    };
    const off = api().onEvent((e) => {
      if (e.type === 'calendar') refresh();
    });
    window.addEventListener('focus', refresh);
    return () => {
      off();
      window.removeEventListener('focus', refresh);
      if (timer) clearTimeout(timer);
    };
  }, [loadInfo, loadEvents]);

  // How the next view enters: a different view fades through; moving in time slides from the travel direction
  const motion = useRef<{ mode: string; cursor: DayKey; kind: 'fade' | 'next' | 'prev' }>({ mode, cursor, kind: 'fade' });
  if (motion.current.mode !== mode) motion.current = { mode, cursor, kind: 'fade' };
  // Day keys are YYYY-MM-DD, so string order is date order
  else if (motion.current.cursor !== cursor) motion.current = { mode, cursor, kind: cursor > motion.current.cursor ? 'next' : 'prev' };
  const viewMotion = motion.current.kind;
  const step = (dir: number) => setCursor((c) => (mode === 'month' ? addMonths(c, dir) : mode === 'week' ? addDays(c, dir * 7) : addDays(c, dir * 30)));
  const openEvent = (event: CalendarEvent, el: Element) => {
    setDayList(null);
    setDetail({ event, anchor: el.getBoundingClientRect() });
  };
  const newEvent = (r: EditorRequest) => {
    setDetail(null);
    setDayList(null);
    setEditor(r);
  };

  const syncAll = async () => {
    if (!info?.accounts?.length) return;
    setSyncing(true);
    try {
      await Promise.all(info.accounts.map((a) => api().calendar.syncAccount(a.id)));
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setSyncing(false);
      void loadInfo();
      void loadEvents();
    }
  };

  // Keyboard: T today, ← → previous / next, N new event, M / W / A switch view
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]') || e.ctrlKey || e.metaKey || e.altKey) return;
      if (document.querySelector('.dialog, .cal-float')) return;
      const handlers: Record<string, () => void> = {
        t: () => setCursor(todayKey()),
        ArrowLeft: () => step(-1),
        ArrowRight: () => step(1),
        n: () => newEvent({ day: cursor }),
        m: () => setMode('month'),
        w: () => setMode('week'),
        a: () => setMode('agenda'),
      };
      const h = handlers[e.key];
      if (h) {
        e.preventDefault();
        h();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const noAccounts = info && !info.accounts?.length;
  const title = mode === 'month' ? monthTitle(cursor) : mode === 'week' ? weekTitle(cursor) : t('接下来的日程');

  return (
    <div className="calview">
      <CalSidebar info={info} cursor={cursor} range={range} onPick={setCursor} onNew={() => newEvent({ day: cursor })} onChanged={() => void Promise.all([loadInfo(), loadEvents()])} onOpenSettings={onOpenSettings} />
      <section className="pane calmain">
        <header className="calmain__bar">
          <button className="btn btn--small" onClick={() => setCursor(todayKey())} title={t('今天（T）')}>
            {t('今天')}
          </button>
          <div className="calmain__nav">
            <button className="btn btn--icon btn--quiet" aria-label={t('上一页')} title={t('上一页（←）')} onClick={() => step(-1)}>
              <Icon name="back" size={16} />
            </button>
            <button className="btn btn--icon btn--quiet" aria-label={t('下一页')} title={t('下一页（→）')} onClick={() => step(1)}>
              <Icon name="right" size={16} />
            </button>
          </div>
          <h1 className="calmain__title">{title}</h1>
          {(!events || syncing) && <span className="spinner" />}
          <div className="calmain__spacer" />
          <button className="btn btn--icon btn--quiet" aria-label={t('立即同步日历')} title={t('立即同步日历')} disabled={syncing || !info?.accounts?.length} onClick={() => void syncAll()}>
            <Icon name="refresh" size={16} />
          </button>
          <div className="segmented" role="group" aria-label={t('视图')}>
            {(
              [
                ['month', t('月'), 'M'],
                ['week', t('周'), 'W'],
                ['agenda', t('日程'), 'A'],
              ] as const
            ).map(([id, label, key]) => (
              <button key={id} aria-pressed={mode === id} title={`${label} (${key})`} onClick={() => setMode(id)}>
                {label}
              </button>
            ))}
          </div>
        </header>
        {error && (
          <div className="notice notice--error calmain__notice">
            <Icon name="alert" size={14} /> {error}
          </div>
        )}
        {noAccounts ? (
          <div className="empty">
            <div className="empty__art empty__art--small" aria-hidden>
              <Mascot />
            </div>
            <h2>{t('还没有连接日历')}</h2>
            <p className="muted">{t('连接 Google 日历或者 iCloud、QQ 邮箱等 CalDAV 日历，所有日历的活动会放在这里一起看。和邮箱账户互不影响。')}</p>
            <button className="btn btn--primary" onClick={onOpenSettings}>
              <Icon name="calendarPlus" size={16} /> {t('连接日历')}
            </button>
          </div>
        ) : (
          <div key={`${mode}:${range.from}`} className={`calmain__view calmain__view--${viewMotion}`}>
        {mode === 'month' ? (
          <MonthView cursor={cursor} events={events ?? []} look={look} onOpen={openEvent} onNew={(day) => newEvent({ day })} onMore={(day, el) => setDayList({ day, anchor: el.getBoundingClientRect() })} />
        ) : mode === 'week' ? (
          <WeekView cursor={cursor} events={events ?? []} look={look} onOpen={openEvent} onNew={newEvent} />
        ) : (
          <AgendaView from={range.from} days={AGENDA_DAYS} events={events} look={look} onOpen={openEvent} />
        )}
          </div>
        )}
      </section>
      {detail && (
        <FloatingCard anchor={detail.anchor} onClose={() => setDetail(null)}>
          <EventCard
            event={detail.event}
            look={look}
            onClose={() => setDetail(null)}
            onEdit={() => newEvent({ event: detail.event })}
            onDeleted={() => {
              setDetail(null);
              void loadEvents();
            }}
          />
        </FloatingCard>
      )}
      {dayList && (
        <FloatingCard anchor={dayList.anchor} onClose={() => setDayList(null)} className="cal-float--day">
          <div className="cal-daylist">
            <h3>{dayTitle(dayList.day)}</h3>
            {(eventsByDay(events ?? [], [dayList.day]).get(dayList.day) ?? []).map((e) => (
              <EventChip key={e.id} event={e} look={look} day={dayList.day} onOpen={openEvent} />
            ))}
          </div>
        </FloatingCard>
      )}
      <EventEditor
        request={editor}
        info={info}
        onClose={() => setEditor(null)}
        onSaved={(saved) => {
          setEditor(null);
          // Jump to where the event is if it's off screen
          const { first } = eventDays(saved);
          if (first < range.from || first >= range.to) setCursor(first);
          void loadEvents();
        }}
      />
    </div>
  );
}

function weekTitle(cursor: DayKey): string {
  const days = weekDays(cursor, weekStart());
  const a = parseDay(days[0]!);
  const b = parseDay(days[6]!);
  if (language() === 'zh') {
    return a.getMonth() === b.getMonth() ? `${a.getFullYear()}年${a.getMonth() + 1}月 ${a.getDate()}–${b.getDate()}日` : `${a.getMonth() + 1}月${a.getDate()}日 – ${b.getMonth() + 1}月${b.getDate()}日`; // i18n-ok
  }
  const f = (d: Date) => d.toLocaleDateString(locale(), { month: 'short', day: 'numeric' });
  return `${f(a)} – ${f(b)}, ${b.getFullYear()}`;
}

// ---------------- Sidebar ----------------

function CalSidebar({
  info,
  cursor,
  range,
  onPick,
  onNew,
  onChanged,
  onOpenSettings,
}: {
  info: CalendarInfo | null;
  cursor: DayKey;
  range: { from: DayKey; to: DayKey };
  onPick: (d: DayKey) => void;
  onNew: () => void;
  onChanged: () => void;
  onOpenSettings: () => void;
}) {
  const [month, setMonth] = useState(cursor);
  useEffect(() => setMonth(cursor), [cursor]);
  const [colorFor, setColorFor] = useState<string | null>(null);
  useEffect(() => {
    if (!colorFor) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.calside__colors, .calside__color')) setColorFor(null);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [colorFor]);
  const grid = monthGrid(month, weekStart());
  const today = todayKey();
  const names = weekdayNames();
  const writable = (info?.calendars ?? []).some((c) => !c.readOnly);

  const update = async (c: CalendarSource, patch: { hidden?: boolean; color?: string }) => {
    try {
      await api().calendar.updateCalendar(c.id, patch);
      onChanged();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <aside className="calside">
      <button className="btn btn--primary sidebar__compose" disabled={!writable} onClick={onNew} title={writable ? t('新建活动（N）') : t('先连接一个可以写入的日历')}>
        <Icon name="plus" size={18} /> {t('新建活动')}
      </button>
      <div className="minimonth">
        <div className="minimonth__head">
          <strong>{monthTitle(month)}</strong>
          <button className="btn btn--icon btn--quiet" aria-label={t('上个月')} onClick={() => setMonth(addMonths(month, -1))}>
            <Icon name="back" size={14} />
          </button>
          <button className="btn btn--icon btn--quiet" aria-label={t('下个月')} onClick={() => setMonth(addMonths(month, 1))}>
            <Icon name="right" size={14} />
          </button>
        </div>
        <div className="minimonth__grid" role="grid">
          {names.map((n) => (
            <span key={n} className="minimonth__dow">
              {n.replace(/^周/, '') /* i18n-ok strips the Chinese weekday prefix */}
            </span>
          ))}
          {grid.map((d) => (
            <button
              key={d}
              className={`minimonth__day ${d.slice(0, 7) !== month.slice(0, 7) ? 'is-other' : ''} ${d === today ? 'is-today' : ''} ${d >= range.from && d < range.to ? 'is-range' : ''} ${d === cursor ? 'is-cursor' : ''}`}
              onClick={() => onPick(d)}
            >
              {Number(d.slice(8))}
            </button>
          ))}
        </div>
      </div>
      <div className="calside__list">
        {(info?.accounts ?? []).map((a) => {
          const own = (info?.calendars ?? []).filter((c) => c.accountId === a.id);
          return (
            <div key={a.id} className="calside__acc">
              <div className="calside__acc-head" title={a.error ?? undefined}>
                <Icon name={a.provider === 'google' ? 'calendar' : 'layers'} size={14} />
                <span className="calside__acc-name">{a.name}</span>
                {a.error && (
                  <button className={`settings__flag settings__flag--${a.needsAuth ? 'error' : 'warn'} calside__flag`} aria-label={a.error} title={a.error} onClick={onOpenSettings} />
                )}
              </div>
              {own.map((c) => (
                <div key={c.id} className={`calside__cal ${c.hidden ? 'is-hidden' : ''}`} style={{ '--cal': c.color } as React.CSSProperties}>
                  <button className="calside__check" role="checkbox" aria-checked={!c.hidden} aria-label={c.hidden ? t('显示 {name}', { name: c.name }) : t('隐藏 {name}', { name: c.name })} onClick={() => void update(c, { hidden: !c.hidden })}>
                    {!c.hidden && <Icon name="check" size={12} />}
                  </button>
                  <span className="calside__name" title={c.name}>
                    {c.name}
                  </span>
                  {c.readOnly && <span className="tag tag--dim">{t('只读')}</span>}
                  <button className="calside__color" data-popover-anchor aria-label={t('换颜色')} title={t('换颜色')} onClick={() => setColorFor(colorFor === c.id ? null : c.id)}>
                    <Icon name="palette" size={13} />
                  </button>
                  {colorFor === c.id && (
                    <div className="calside__colors" role="menu">
                      {CALENDAR_COLORS.map((col) => (
                        <button
                          key={col}
                          className={`calside__swatch ${col === c.color ? 'is-on' : ''}`}
                          style={{ background: col }}
                          aria-label={col}
                          onClick={() => {
                            setColorFor(null);
                            void update(c, { color: col });
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <button className="nav-item calside__manage" onClick={onOpenSettings}>
        <Icon name="sliders" size={15} /> {t('管理日历账户')}
      </button>
    </aside>
  );
}

// ---------------- Chips ----------------

function EventChip({ event, look, day, onOpen, compact = false }: { event: CalendarEvent; look: CalendarLookup; day: DayKey; onOpen: (e: CalendarEvent, el: Element) => void; compact?: boolean }) {
  const color = look.calendars.get(event.calendarId)?.color ?? 'var(--violet)';
  const block = isAllDayLike(event);
  const { first } = eventDays(event);
  // A multi-day timed event shows its start time only on its first day
  const time = event.allDay || (block && first !== day) ? null : clockOf(event.start);
  return (
    <button
      className={`cal-chip ${block ? 'cal-chip--block' : ''} ${compact ? 'cal-chip--compact' : ''}`}
      style={{ '--cal': color } as React.CSSProperties}
      title={event.title}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(event, e.currentTarget);
      }}
    >
      {!block && <span className="cal-chip__dot" aria-hidden />}
      {time && <span className="cal-chip__time">{time}</span>}
      <span className="cal-chip__title">{event.title || t('(无标题)')}</span>
      {event.recurring && <Icon name="refresh" size={11} className="cal-chip__rep" />}
    </button>
  );
}

// ---------------- Month ----------------

const MONTH_ROWS_CHIPS = 3;

function MonthView({
  cursor,
  events,
  look,
  onOpen,
  onNew,
  onMore,
}: {
  cursor: DayKey;
  events: CalendarEvent[];
  look: CalendarLookup;
  onOpen: (e: CalendarEvent, el: Element) => void;
  onNew: (day: DayKey) => void;
  onMore: (day: DayKey, el: Element) => void;
}) {
  const grid = monthGrid(cursor, weekStart());
  const byDay = eventsByDay(events, grid);
  const today = todayKey();
  return (
    <div className="month">
      <div className="month__dows">
        {weekdayNames().map((n) => (
          <span key={n}>{n}</span>
        ))}
      </div>
      <div className="month__grid">
        {grid.map((d) => {
          const list = byDay.get(d) ?? [];
          const shown = list.length > MONTH_ROWS_CHIPS + 1 ? list.slice(0, MONTH_ROWS_CHIPS) : list;
          const more = list.length - shown.length;
          const date = parseDay(d);
          return (
            <div key={d} className={`month__day ${d.slice(0, 7) !== cursor.slice(0, 7) ? 'is-other' : ''} ${date.getDay() === 0 || date.getDay() === 6 ? 'is-weekend' : ''}`} onClick={() => onNew(d)} title={t('点空白处新建活动')}>
              <span className={`month__num ${d === today ? 'is-today' : ''}`}>{date.getDate() === 1 && language() !== 'zh' ? date.toLocaleDateString(locale(), { month: 'short', day: 'numeric' }) : date.getDate()}</span>
              <div className="month__chips">
                {shown.map((e) => (
                  <EventChip key={e.id} event={e} look={look} day={d} onOpen={onOpen} compact />
                ))}
                {more > 0 && (
                  <button
                    className="month__more"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMore(d, e.currentTarget);
                    }}
                  >
                    {t('+{n} 更多', { n: more })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Week ----------------

function WeekView({ cursor, events, look, onOpen, onNew }: { cursor: DayKey; events: CalendarEvent[]; look: CalendarLookup; onOpen: (e: CalendarEvent, el: Element) => void; onNew: (r: EditorRequest) => void }) {
  const days = weekDays(cursor, weekStart());
  const byDay = eventsByDay(events, days);
  const today = todayKey();
  const scroller = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  // Start scrolled to 7:00
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 7 * HOUR_PX - 8;
  }, []);
  const fmt = new Intl.DateTimeFormat(locale(), { weekday: 'short' });
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return (
    <div className="week">
      <div className="week__head">
        <span className="week__gutter" />
        {days.map((d) => (
          <div key={d} className={`week__dayhead ${d === today ? 'is-today' : ''}`}>
            <span>{fmt.format(parseDay(d))}</span>
            <strong>{parseDay(d).getDate()}</strong>
          </div>
        ))}
      </div>
      <div className="week__allday">
        <span className="week__gutter muted">{t('全天')}</span>
        {days.map((d) => (
          <div key={d} className="week__allday-cell" onClick={() => onNew({ day: d, allDay: true })}>
            {(byDay.get(d) ?? []).filter(isAllDayLike).map((e) => (
              <EventChip key={e.id} event={e} look={look} day={d} onOpen={onOpen} compact />
            ))}
          </div>
        ))}
      </div>
      <div className="week__scroll" ref={scroller}>
        <div className="week__body" style={{ height: 24 * HOUR_PX }}>
          <div className="week__gutter week__hours">
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h} style={{ top: h * HOUR_PX }}>
                {h ? `${String(h).padStart(2, '0')}:00` : ''}
              </span>
            ))}
          </div>
          {days.map((d) => {
            const timed = (byDay.get(d) ?? []).filter((e) => !isAllDayLike(e));
            const placed = layoutDay(timed.map((e) => ({ item: e, ...minutesOn(e, d) })));
            return (
              <div
                key={d}
                className={`week__col ${d === today ? 'is-today' : ''}`}
                onClick={(ev) => {
                  const box = ev.currentTarget.getBoundingClientRect();
                  const minute = Math.max(0, Math.min(23 * 60 + 30, Math.floor(((ev.clientY - box.top) / HOUR_PX) * 2) * 30));
                  onNew({ day: d, minute });
                }}
              >
                {placed.map((p) => {
                  const color = look.calendars.get(p.item.calendarId)?.color ?? 'var(--violet)';
                  const height = Math.max(18, ((p.to - p.from) / 60) * HOUR_PX - 2);
                  return (
                    <button
                      key={p.item.id}
                      className="week__event"
                      style={{ top: (p.from / 60) * HOUR_PX, height, left: `calc(${(p.col / p.cols) * 100}% + 2px)`, width: `calc(${100 / p.cols}% - 4px)`, '--cal': color } as React.CSSProperties}
                      title={p.item.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(p.item, e.currentTarget);
                      }}
                    >
                      <strong>{p.item.title || t('(无标题)')}</strong>
                      {height > 30 && (
                        <span>
                          {clockOf(p.item.start)}–{clockOf(p.item.end)}
                        </span>
                      )}
                    </button>
                  );
                })}
                {d === today && <div className="week__now" style={{ top: (nowMin / 60) * HOUR_PX }} aria-hidden />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------- Agenda ----------------

function AgendaView({ from, days, events, look, onOpen }: { from: DayKey; days: number; events: CalendarEvent[] | null; look: CalendarLookup; onOpen: (e: CalendarEvent, el: Element) => void }) {
  const keys = Array.from({ length: days }, (_, i) => addDays(from, i));
  const byDay = eventsByDay(events ?? [], keys);
  const filled = keys.filter((k) => (byDay.get(k) ?? []).length > 0);
  const today = todayKey();
  if (events && !filled.length) {
    return (
      <div className="empty">
        <h2>{t('接下来两个月没有安排')}</h2>
        <p className="muted">{t('隐藏的日历里的活动不会显示。')}</p>
      </div>
    );
  }
  return (
    <div className="agenda">
      {filled.map((k) => (
        <div key={k} className="agenda__day">
          <div className={`agenda__date ${k === today ? 'is-today' : ''}`}>
            <strong>{parseDay(k).getDate()}</strong>
            <span>{dayKey(new Date()) === k ? t('今天') : parseDay(k).toLocaleDateString(locale(), { month: 'short', weekday: 'short' })}</span>
          </div>
          <div className="agenda__list">
            {(byDay.get(k) ?? []).map((e) => {
              const color = look.calendars.get(e.calendarId)?.color ?? 'var(--violet)';
              const cal = look.calendars.get(e.calendarId);
              return (
                <button key={e.id} className="agenda__item" style={{ '--cal': color } as React.CSSProperties} onClick={(ev) => onOpen(e, ev.currentTarget)}>
                  <span className="agenda__time">{isAllDayLike(e) ? t('全天') : `${clockOf(e.start)}–${clockOf(e.end)}`}</span>
                  <span className="agenda__bar" aria-hidden />
                  <span className="agenda__text">
                    <strong>{e.title || t('(无标题)')}</strong>
                    {(e.location || cal) && <span className="muted">{[e.location, cal?.name].filter(Boolean).join(' · ')}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
